<!-- coursecard -->
<script setup lang="ts">
import type { CreneauInstance, Lieux } from '~/types/directus';
import type { InstanceWithMsg } from '~/components/block/Inscriptions.vue';

type DirectusFile = string | { id: string }; // au cas où

const props = defineProps<{ course: CreneauInstance }>();

const config = useRuntimeConfig();

const capacity = computed<number>(
  () => (props.course.places_max ?? props.course.creneau_type?.places_max ?? 0) as number,
);
const taken = computed<number>(() => (typeof props.course.nb_inscrits === 'number' ? props.course.nb_inscrits! : 0));
const remaining = computed<number>(() => Math.max(0, capacity.value - taken.value));
const capacityText = computed<string>(() => `${capacity.value} place${capacity.value > 1 ? 's' : ''}`);
const isFull = computed<boolean>(() => !!props.course.complet || remaining.value <= 0);
const onlineSignupEnabled = computed<boolean>(() => props.course.inscription_en_ligne !== false);

const price = computed<number | null>(() => props.course.prix_override ?? props.course.creneau_type?.prix ?? null);
const priceLabel = computed<string | null>(() => {
  if (price.value === null) return null;
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(price.value);
});

const badgeStyle = computed<Record<string, string>>(() => ({
  background: props.course.creneau_type?.couleur_fond || '#eee',
  color: props.course.creneau_type?.couleur_texte || '#111',
}));

const message = computed<string | null>(() => {
  if (props.course.message_override_enabled && props.course.message_override_text) {
    return props.course.message_override_text;
  }
  return null;
});
const display_end = true;

const dateLabel = computed<string>(() => {
  const tz = (config.public.tz as string) || 'Europe/Paris';
  const start = new Date(props.course.start_datetime);
  const duree = props.course.creneau_type.duree_minutes;
  let end = duree ? new Date(start.getTime() + duree * 60000) : null;
  if (!display_end) end = null;
  const fmt = new Intl.DateTimeFormat('fr-FR', {
    timeZone: tz,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  const fmtTime = new Intl.DateTimeFormat('fr-FR', { timeZone: tz, hour: '2-digit', minute: '2-digit' });
  return end ? `${fmt.format(start)} – ${fmtTime.format(end)}` : fmt.format(start);
});

const timeLabel = computed<string>(() => {
  const tz = (config.public.tz as string) || 'Europe/Paris';
  const start = new Date(props.course.start_datetime);
  const duree = props.course.creneau_type.duree_minutes;
  let end = duree ? new Date(start.getTime() + duree * 60000) : null;
  if (!display_end) end = null;
  const fmtTime = new Intl.DateTimeFormat('fr-FR', { timeZone: tz, hour: '2-digit', minute: '2-digit' });
  return end ? `${fmtTime.format(start)} – ${fmtTime.format(end)}` : fmtTime.format(start);
});

const dureeHeures = computed<string>(() => {
  const duree = props.course.creneau_type.duree_minutes;
  if (!duree) return '';
  const heures = Math.floor(duree / 60);
  const minutes = duree % 60;
  return `${heures}h${minutes < 10 ? '0' : ''}${minutes}`;
});

const siteData = useState('siteData', () => useSiteData());
const logoSymbol = computed(() => siteData.value?.globals?.logo_symbole || '');

// ----- Modal "Plus d'infos" (cours)
const showModal = ref(false);
const modalCourse = ref<CreneauInstance | null>(null);
const closeBtnRef = ref<HTMLButtonElement | null>(null);
function openInfo(course: CreneauInstance) {
  modalCourse.value = course;
  showModal.value = true;
  nextTick(() => closeBtnRef.value?.focus());
}
function closeInfo() {
  showModal.value = false;
}

// ----- Modal "Détails du lieu"
const showLieuModal = ref(false);
const lieuForModal = ref<Lieux | null>(null);

function normalizeLieu(input: any): Lieux | null {
  if (!input) return null;
  if (typeof input === 'object') {
    // On copie ce qu'on connaît, en gardant des undefined/null si absent
    return {
      id: input.id,
      nom: input.nom ?? 'Lieu',
      adresse: input.adresse ?? null,
      capacite: input.capacite ?? null,
      couleur_fond_default: input.couleur_fond_default ?? null,
      couleur_texte_default: input.couleur_texte_default ?? null,
      notes: input.notes ?? null,
      illustration: typeof input.illustration === 'object' ? input.illustration?.id : (input.illustration ?? null),
    };
  }
  // Si c'est un string (id), on ne fetch pas ici pour rester simple.
  return {
    id: input as string,
    nom: 'Lieu',
    adresse: null,
    capacite: null,
    couleur_fond_default: null,
    couleur_texte_default: null,
    notes: null,
    illustration: null,
  };
}

function openLieuDetails() {
  const normalized = normalizeLieu((props.course as any).lieu);
  lieuForModal.value = normalized;
  showLieuModal.value = true;

  // TODO: si tu veux auto-fetch quand seul l'ID est présent :
  // if (normalized && normalized.nom === 'Lieu' && normalized.id) {
  //   $fetch(`/api/lieux/${normalized.id}`).then((full) => {
  //     lieuForModal.value = normalizeLieu(full);
  //   }).catch(() => {});
  // }
}
function closeLieuDetails() {
  showLieuModal.value = false;
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && showModal.value) closeInfo();
  if (e.key === 'Escape' && showLieuModal.value) closeLieuDetails();
}
onMounted(() => window.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown));

const emit = defineEmits<{ (e: 'signup', c: CreneauInstance): void }>();
function handleSignup() {
  if (modalCourse.value) {
    closeInfo();
  }
  // close lieu modal
  closeLieuDetails();
  // emit signup
  if (modalCourse.value) {
    emit('signup', modalCourse.value);
  }
}
</script>

<template>
  <!-- {{ course }} -->
  <div
    class="rounded-xl border-4 p-4 flex flex-col gap-4 items-start bg-ocreClair border-ocreClair hover:shadow-xl transition-shadow duration-200 w-full sm:max-w-[340px] min-w-[90%] sm:min-w-[unset] relative cursor-pointer min-h-[200px]"
    @click="openInfo(course)"
    aria-label="Voir plus d'infos sur le cours"
    title="Voir plus d'infos sur le cours"
    :class="
      new Date(course.start_datetime) < new Date() || course.status === 'cancelled'
        ? 'opacity-60 pointer-events-none'
        : ''
    "
  >
    <div
      v-if="course.status === 'cancelled'"
      class="text-red-700 font-medium mr-2 w-full text-center mb-2 border-2 rounded border-red-700/30"
    >
      <!-- <font-awesome :icon="['fas', 'circle-xmark']" class="mr-1" /> -->
      Annulé
    </div>
    <header class="flex items-center flex-wrap gap-2 justify-between w-full">
      <div class="flex items-center gap-1">
        <DirectusImage
          :uuid="logoSymbol"
          :alt="'Logo'"
          class="inline-block w-6 h-6 mr-0"
          v-if="logoSymbol"
          style="filter: invert(33%) sepia(54%) saturate(476%) hue-rotate(0deg) brightness(95%) contrast(105%)"
        />
        <h3 class="font-semibold">
          {{ course.creneau_type?.titre || 'Cours' }}
        </h3>
      </div>

      <span class="text-xs px-2 py-1 rounded bg-gray">{{ capacityText }}</span>
    </header>

    <!-- <button class="px-3 py-1 rounded shadow border border-gray-500 text-sm -mt-2" @click="openInfo(course)"> -->
    <!--   Plus d'infos -->
    <!-- </button> -->
    <div v-if="course.message_override_enabled" class="my-2">
      <p class="text-sm opacity-80 italic">{{ course.message_override_text }}</p>
    </div>
    <div v-else-if="course.creneau_type.descriptif" class="my-2">
      <p class="text-sm opacity-80">{{ course.creneau_type.descriptif }}</p>
    </div>

    <div class="flex items-center justify-between w-full mt-4 mt-auto">
      <div class="text-md flex items-center gap-1">
        <span>{{ timeLabel }}</span>
        <span v-if="!display_end">({{ dureeHeures }})</span>
      </div>

      <!-- Nom du lieu + bouton pour détails -->
      <div v-if="course.lieu?.nom || course.lieu" class="flex items-center gap-2">
        <!-- couleur de fond et texte par défaut du lieu -->
        <div
          class="rounded shadow px-2 py-1 text-sm flex items-center gap-1 cursor-pointer"
          :style="{
            background: course.lieu?.couleur_fond_default ?? '#eee',
            color: course.lieu?.couleur_texte_default ?? '#111',
          }"
        >
          <button
            class="text-xs decoration-dotted hover:decoration-solid"
            @click.stop="openLieuDetails"
            aria-label="Voir les détails du lieu"
            title="Voir le lieu"
          >
            <span class="truncate max-w-[9rem]">{{ (course.lieu as any)?.nom || 'Lieu' }}</span>
            <font-awesome :icon="['fas', 'location-dot']" class="" />
          </button>
        </div>
      </div>
    </div>

    <!-- <button -->
    <!--   class="px-3 py-2 rounded bg-ocreMedium disabled:opacity-50 w-full -mt-2" -->
    <!--   :disabled="isFull" -->
    <!--   @click="$emit('signup', course)" -->
    <!-- > -->
    <!--   S'inscrire -->
    <!-- </button> -->
    <!-- messages -->
    <!-- {{ course.global_message }} -->
    <!-- <div v-if="course.global_message.length" class=""> -->
    <!--   <font-awesome -->
    <!--     :icon="['fas', 'message']" -->
    <!--     style="font-size: 13px" -->
    <!--     class="absolute -top-4 -right-4 text-ocreMedium" -->
    <!--   /> -->
    <!-- </div> -->
  </div>

  <!-- Modal cours -->
  <Teleport to="body">
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center"
      aria-labelledby="course-modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div class="absolute inset-0 bg-black/30" @click="closeInfo" />
      <div class="relative z-10 w-[min(92vw,560px)] rounded-lg bg-beigeClair p-6 shadow-xl" @click.stop>
        <header class="flex items-start justify-between gap-4">
          <h2 id="course-modal-title" class="text-xl font-bold bg-ocreClair p-2 px-4 rounded flex items-center gap-2">
            <DirectusImage
              :uuid="logoSymbol"
              :alt="'Logo'"
              class="inline-block w-6 h-6 mr-0"
              v-if="logoSymbol"
              style="filter: invert(33%) sepia(54%) saturate(476%) hue-rotate(0deg) brightness(95%) contrast(105%)"
            />
            <span>{{ modalCourse?.creneau_type?.titre || 'Cours' }}</span>
          </h2>
          <button ref="closeBtnRef" class="ml-auto rounded p-1 text-sm btn" @click="closeInfo">
            <font-awesome :icon="['fas', 'xmark']" />
          </button>
        </header>
        <div v-if="modalCourse.message_override_enabled" class="my-2">
          <p class="text-sm opacity-80 italic">{{ modalCourse.message_override_text }}</p>
        </div>
        <div v-else-if="course.creneau_type.descriptif" class="my-2">
          <p class="text-sm opacity-80">{{ modalCourse.creneau_type.descriptif }}</p>
        </div>

        <div class="flex flex-row items-center gap-4">
          <div v-if="modalCourse?.creneau_type?.infos_image" class="mt-4">
            <DirectusImage
              :uuid="modalCourse?.creneau_type?.infos_image"
              :alt="modalCourse?.creneau_type?.titre || 'Image du cours'"
              class="w-[200px] h-[200px] object-contain rounded"
              width="200px"
              height="200px"
            />
          </div>
          <section class="mt-4 space-y-2 text-sm">
            <p>{{ modalCourse?.creneau_type.infos_description || 'Aucune description disponible.' }}</p>
            <p class="!mt-4">
              <span class="font-medium">Date :</span>
              {{ dateLabel }}
            </p>
            <p>
              <span class="font-medium">Durée :</span>
              {{ dureeHeures }}
            </p>
            <!-- prix -->
            <p v-if="priceLabel">
              <span class="font-medium">Prix :</span>
              {{ priceLabel }}
            </p>
            <div class="flex items-center gap-1">
              <span class="font-medium">Lieu :</span>
              <div
                class="rounded shadow px-2 py-1 text-sm flex items-center gap-1 cursor-pointer w-fit"
                :style="{
                  background: course.lieu?.couleur_fond_default ?? '#eee',
                  color: course.lieu?.couleur_texte_default ?? '#111',
                }"
              >
                <button
                  class="text-xs decoration-dotted hover:decoration-solid"
                  @click.stop="openLieuDetails"
                  aria-label="Voir les détails du lieu"
                  title="Voir le lieu"
                >
                  <span class="truncate max-w-[9rem]">{{ (course.lieu as any)?.nom || 'Lieu' }}</span>
                  <font-awesome :icon="['fas', 'location-dot']" class="" />
                </button>
              </div>
            </div>
          </section>
        </div>

        <footer class="mt-6 flex justify-end gap-2">
          <!-- closeInfo(); -->
          <button
            v-if="onlineSignupEnabled"
            class="px-3 py-2 rounded bg-ocreMedium disabled:opacity-50 w-full"
            :disabled="isFull || new Date(modalCourse?.start_datetime || '') < new Date()"
            @click="handleSignup"
          >
            S'inscrire
          </button>
          <button
            v-else
            type="button"
            class="w-full cursor-not-allowed rounded bg-slate-300 px-3 py-2 text-slate-600"
            disabled
          >
            Pas d'inscription en ligne
          </button>
        </footer>
      </div>
    </div>
  </Teleport>

  <!-- Modal détails du lieu -->
  <Teleport to="body">
    <div
      v-if="showLieuModal"
      class="fixed inset-0 z-50 flex items-center justify-center"
      aria-labelledby="lieu-modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div class="absolute inset-0 bg-black/30" @click="closeLieuDetails" />
      <div class="relative z-10 w-[min(92vw,560px)] rounded-lg bg-beigeClair p-6 shadow-xl" @click.stop>
        <header class="flex items-start justify-between gap-4">
          <h2 id="lieu-modal-title" class="text-xl font-bold bg-ocreClair p-2 px-4 rounded">
            {{ lieuForModal?.nom || 'Lieu' }}
          </h2>
          <button class="ml-auto rounded p-1 text-sm btn" @click="closeLieuDetails" aria-label="Fermer">
            <font-awesome :icon="['fas', 'xmark']" />
          </button>
        </header>

        <div v-if="lieuForModal?.adresse" class="mt-2">
          {{ lieuForModal.adresse }}
        </div>
        <div class="mt-4 flex gap-4">
          <div v-if="lieuForModal?.illustration" class="shrink-0">
            <DirectusImage
              :uuid="
                typeof lieuForModal.illustration === 'object'
                  ? (lieuForModal.illustration as any).id
                  : lieuForModal.illustration
              "
              :alt="lieuForModal?.nom || 'Illustration du lieu'"
              class="w-[200px] h-[200px] object-cover rounded"
              width="200px"
              height="200px"
            />
          </div>

          <section class="space-y-2 text-sm">
            <p v-if="lieuForModal?.notes">
              {{ lieuForModal.notes }}
            </p>

            <div
              class="flex gap-2 items-center"
              v-if="lieuForModal?.couleur_fond_default || lieuForModal?.couleur_texte_default"
            ></div>
          </section>
        </div>

        <footer class="mt-6 flex justify-end">
          <button class="px-3 py-2 rounded border border-gray-500" @click="closeLieuDetails">Fermer</button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>
